import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

export default function Quartalsuebersicht() {
  const [quarterly, setQuarterly] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuarterly();
  }, []);

  const loadQuarterly = async () => {
    try {
      const response = await axios.get(`${API}/quarterly`);
      setQuarterly(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Quartalsübersicht");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-slate-600">Lädt...</div>
      </div>
    );
  }

  const quarters = Object.entries(quarterly || {}).sort((a, b) => b[0].localeCompare(a[0]));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/dashboard">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center">
            <Calendar className="w-8 h-8 mr-3 text-cyan-600" />
            Quartalsübersicht
          </h1>
          <p className="text-slate-500 mb-8">Aggregierte Daten pro Quartal</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {quarters.map(([quarterKey, data]) => (
              <div 
                key={quarterKey}
                className="bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200 rounded-xl p-6"
              >
                <h3 className="text-2xl font-bold text-slate-800 mb-4">{quarterKey}</h3>
                
                <div className="space-y-3">
                  {/* Zugänge */}
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
                        <span className="font-semibold text-slate-700">Zugänge</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          {data.incoming_quantity || 0} Stk
                        </div>
                        <div className="text-sm text-slate-500">
                          {data.incoming_value?.toFixed(2) || '0.00'} €
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Abgänge */}
                  <div className="bg-white rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <TrendingDown className="w-5 h-5 mr-2 text-red-600" />
                        <span className="font-semibold text-slate-700">Abgänge</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-red-600">
                          {data.outgoing_quantity || 0} Stk
                        </div>
                        <div className="text-sm text-slate-500">
                          {data.outgoing_value?.toFixed(2) || '0.00'} €
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Differenz */}
                  <div className={`bg-white rounded-lg p-4 border-2 ${
                    (data.differenz || 0) >= 0 ? 'border-green-300' : 'border-red-300'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="font-semibold text-slate-700">Differenz (Einnahmen - Ausgaben)</span>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          (data.differenz || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(data.differenz || 0) >= 0 ? '+' : ''}{data.differenz?.toFixed(2) || '0.00'} €
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Steuer */}
                  <div className="bg-white rounded-lg p-4 border border-orange-200">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-700">Steuer Eingänge</span>
                        <span className="text-sm text-slate-600">
                          {data.steuer_eingaenge?.toFixed(2) || '0.00'} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-700">Steuer Abgänge</span>
                        <span className="text-sm text-slate-600">
                          {data.steuer_abgaenge?.toFixed(2) || '0.00'} €
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <span className="font-bold text-slate-700">Differenz</span>
                        <span className={`text-lg font-bold ${
                          (data.steuer_differenz || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(data.steuer_differenz || 0) >= 0 ? '+' : ''}{data.steuer_differenz?.toFixed(2) || '0.00'} €
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Punkte */}
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="font-semibold text-slate-700">Punkte (Gesamt)</span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-purple-600">
                          {data.punkte_total?.toFixed(0) || '0'} Pkt
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {quarters.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Noch keine Daten vorhanden
            </div>
          )}
        </div>
      </div>
    </div>
  );
}